const BLOCKED_KEYS = new Set(['username', 'email', 'user_id', 'userId', 'password']);

/** Ids are shown as human-readable context instead of raw values. */
const HIDDEN_KEYS = new Set([
  'duel_id',
  'duel_slug',
  'gameweek_id',
  'winner_player_name',
  'loser_player_name',
  'winner_human_votes',
  'total_human_votes',
  'winner_share',
  'gameweek_number',
]);

function readString(evidence: Record<string, unknown>, key: string): string | null {
  const value = evidence[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readNumber(evidence: Record<string, unknown>, key: string): number | null {
  const value = evidence[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function labelFor(key: string): string {
  return key
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Formats optional server evidence for display without logging private fields.
 * Known duel keys become a readable hero-moment line; anything else falls back to
 * simple `Label: value` pairs. Nested objects and PII keys are skipped.
 */
export function formatGamificationEvidenceSummary(
  evidence: Record<string, unknown> | null | undefined,
): string | null {
  if (!evidence) {
    return null;
  }

  const parts: string[] = [];

  const gameweekNumber = readNumber(evidence, 'gameweek_number');
  if (gameweekNumber != null && gameweekNumber > 0) {
    parts.push(`GW ${gameweekNumber}`);
  }

  const winnerName = readString(evidence, 'winner_player_name');
  const loserName = readString(evidence, 'loser_player_name');
  if (winnerName && loserName) {
    parts.push(`${winnerName} over ${loserName}`);
  } else if (winnerName) {
    parts.push(winnerName);
  }

  const winnerVotes = readNumber(evidence, 'winner_human_votes');
  const totalVotes = readNumber(evidence, 'total_human_votes');
  if (winnerVotes != null && totalVotes != null && totalVotes > 0) {
    parts.push(`${winnerVotes} of ${totalVotes} votes`);
  }

  const winnerShare = readNumber(evidence, 'winner_share');
  if (winnerShare != null) {
    parts.push(`${Math.round(winnerShare * 100)}% crowd`);
  }

  for (const [key, value] of Object.entries(evidence)) {
    if (BLOCKED_KEYS.has(key) || HIDDEN_KEYS.has(key)) {
      continue;
    }

    if (typeof value === 'string' && value.trim()) {
      parts.push(`${labelFor(key)}: ${value.trim()}`);
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      parts.push(`${labelFor(key)}: ${value}`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}
